import React from 'react';
import { Check, X, Mail, MessageCircle, Search, Clock, Users, Zap, ArrowRight, AlertTriangle } from 'lucide-react';
import { useSEO } from '../utils/seo';

const CompareEmail = () => {
  useSEO({
    title: 'TicketDesk vs Shared Email — Why You Need a Help Desk',
    description: 'Stop losing customer emails in shared inboxes. TicketDesk turns Gmail into a proper ticketing system with tracking, assignment, and SLA monitoring.',
    path: '/compare/email',
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">TicketDesk</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-gray-600 hover:text-gray-900">Home</a>
              <a href="/register" className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              TicketDesk vs Regular Email
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Email is free, but the chaos costs you customers. See what you're actually losing by managing support through Gmail alone.
            </p>
          </div>

          {/* Quick Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Regular Email Card */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Regular Gmail</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">$0</div>
                <div className="text-gray-600">per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Free to use</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">You already know how to use it</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Mixed with personal emails</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">No tracking or status</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Team collaboration is messy</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">No multi-channel support</span>
                </div>
              </div>
            </div>

            {/* TicketDesk Card */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 shadow-lg border-2 border-purple-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Actually Organized
                </span>
              </div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">TicketDesk</h3>
                <div className="text-4xl font-bold text-purple-600 mb-2">$29</div>
                <div className="text-gray-600">per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">$29/month investment</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Separate support inbox</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Track every ticket's status</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Team sees everything</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Gmail + Facebook + Trustpilot</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">AI helps write responses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Hidden Cost of "Free" */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            The Real Cost of Managing Support in Gmail
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Email is "free" but you're paying in lost customers and wasted time
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Time Wasted</h3>
              <div className="text-3xl font-bold text-red-600 mb-2">5+ hours</div>
              <p className="text-gray-700 mb-4">per week searching for emails, checking if you responded, figuring out who's handling what</p>
              <div className="bg-white rounded-lg p-3 border-l-4 border-red-500">
                <div className="text-sm text-gray-700">At $50/hour, that's <span className="font-semibold text-red-600">$1,000+/month</span> in lost productivity</div>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Missed Messages</h3>
              <div className="text-3xl font-bold text-red-600 mb-2">15-20%</div>
              <p className="text-gray-700 mb-4">of customer emails get missed or take days to find buried in your inbox</p>
              <div className="bg-white rounded-lg p-3 border-l-4 border-red-500">
                <div className="text-sm text-gray-700">Even <span className="font-semibold text-red-600">one lost customer</span> per month costs more than TicketDesk</div>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Confusion</h3>
              <div className="text-3xl font-bold text-red-600 mb-2">Daily</div>
              <p className="text-gray-700 mb-4">conflicts when multiple people reply to the same customer or duplicate work</p>
              <div className="bg-white rounded-lg p-3 border-l-4 border-red-500">
                <div className="text-sm text-gray-700">Looks unprofessional and <span className="font-semibold text-red-600">frustrates customers</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You're Missing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What Regular Email Can't Do
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Track What's Resolved</h3>
                  <p className="text-gray-700">In email, there's no way to see at a glance what's open, what's being worked on, what's resolved. You just hope you didn't miss anything.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Know Who's Working On What</h3>
                  <p className="text-gray-700">With email, everyone's in the dark. Did Sarah reply? Is Mike handling this? Better send a Slack message to check...</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Handle Multiple Channels</h3>
                  <p className="text-gray-700">Email only shows... email. You're still checking Facebook separately. Still checking Trustpilot separately. Still missing things.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Search Customer History</h3>
                  <p className="text-gray-700">Good luck finding that conversation from 3 months ago. Was it in a thread? Different subject line? Who knows...</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">See Response Times</h3>
                  <p className="text-gray-700">How fast are you really responding? No idea. You're just doing your best and hoping it's enough.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Separate Support from Personal</h3>
                  <p className="text-gray-700">Customer emergencies mixed with newsletters, receipts, and "Hey, let's get lunch sometime" emails. It's chaos.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What TicketDesk Gives You */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What You Get with TicketDesk
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border border-purple-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <MessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Unified Inbox</h3>
              <p className="text-gray-700 mb-4">Every customer message from Gmail, Facebook, and Trustpilot in one place. Nothing gets lost.</p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  See everything at once
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Filter by channel or status
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Search across all channels
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border border-purple-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Visibility</h3>
              <p className="text-gray-700 mb-4">Everyone sees the same dashboard. No more "Did you get that email?" messages.</p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Assign tickets to team members
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  See who's working on what
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Internal notes and collaboration
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border border-purple-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">AI Assistance</h3>
              <p className="text-gray-700 mb-4">Get help writing professional responses. Sound polished even when you're rushed.</p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Suggest complete responses
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Adjust tone and style
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Handle difficult situations
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Examples */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Real Scenarios: Email vs TicketDesk
          </h2>

          <div className="space-y-8">
            {/* Scenario 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500">
                <div className="flex items-center mb-4">
                  <Mail className="w-6 h-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With Regular Email</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"Customer sent urgent issue at 9am. You saw it at 11am but got pulled into a meeting. Forgot about it. Customer sent another email at 3pm, now angry. You're searching through threads trying to remember the context..."</p>
                <div className="text-sm text-red-600 font-medium">Response time: 6+ hours, angry customer</div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <MessageCircle className="w-6 h-6 text-green-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With TicketDesk</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"Customer's message comes in as HIGH priority ticket. You see it immediately, mark it to handle after your meeting. Teammate sees it's urgent and handles it. Customer gets response in 20 minutes. Everyone knows it's resolved."</p>
                <div className="text-sm text-green-600 font-medium">Response time: 20 minutes, happy customer</div>
              </div>
            </div>

            {/* Scenario 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500">
                <div className="flex items-center mb-4">
                  <Mail className="w-6 h-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With Regular Email</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"Customer leaves 1-star review on Trustpilot. You don't check Trustpilot daily. You discover it 5 days later. Now it's on page 1 of Google and you look terrible..."</p>
                <div className="text-sm text-red-600 font-medium">Damage to reputation, no way to catch it early</div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <MessageCircle className="w-6 h-6 text-green-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With TicketDesk</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"Review comes in as ticket immediately. You see it in your dashboard. Respond within an hour with solution. Customer appreciates fast response, updates review to 4 stars."</p>
                <div className="text-sm text-green-600 font-medium">Reputation saved, customer retained</div>
              </div>
            </div>

            {/* Scenario 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500">
                <div className="flex items-center mb-4">
                  <Mail className="w-6 h-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With Regular Email</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"You reply to customer. Sarah also replies because she didn't see your email. Customer gets two different answers. Now confused and thinks you're disorganized. You look unprofessional."</p>
                <div className="text-sm text-red-600 font-medium">Customer confusion, team looking incompetent</div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <MessageCircle className="w-6 h-6 text-green-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">With TicketDesk</h3>
                </div>
                <p className="text-gray-700 italic mb-4">"You claim the ticket. Sarah sees you're handling it. She moves on to other tickets. Customer gets one clear answer. Your team looks professional and coordinated."</p>
                <div className="text-sm text-green-600 font-medium">Professional appearance, efficient teamwork</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Calculation */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Is $29/Month Worth It?
          </h2>

          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200">
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-4 border-b border-purple-200">
                <span className="text-gray-700">Time saved per week (5 hours × $50/hour)</span>
                <span className="text-xl font-bold text-green-600">+$1,000/month</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-purple-200">
                <span className="text-gray-700">Customers retained (just 1 lost customer prevented)</span>
                <span className="text-xl font-bold text-green-600">+$500/month</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-purple-200">
                <span className="text-gray-700">Better reviews & reputation</span>
                <span className="text-xl font-bold text-green-600">Priceless</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-purple-200">
                <span className="text-gray-700">Professional appearance to customers</span>
                <span className="text-xl font-bold text-green-600">Priceless</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b-2 border-purple-300">
                <span className="text-gray-700 font-semibold">Cost of TicketDesk</span>
                <span className="text-xl font-bold text-purple-600">-$29/month</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-xl text-gray-900 font-bold">Net Value</span>
                <span className="text-3xl font-bold text-green-600">+$1,471/month</span>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-8 text-lg">
            Email is free, but it's costing you <span className="font-semibold text-red-600">way more than $29/month</span> in lost time and customers.
          </p>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Start in 2 Minutes
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Your Gmail stays exactly the same - TicketDesk just adds organization on top
          </p>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sign up for TicketDesk</h3>
                <p className="text-gray-700">Start with the free plan (10 tickets/month). No credit card needed.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Gmail in 60 seconds</h3>
                <p className="text-gray-700">Click "Connect Gmail" → authorize. Your emails start flowing into TicketDesk immediately.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Keep using Gmail however you want</h3>
                <p className="text-gray-700">Your email works exactly the same. TicketDesk just organizes your support emails into tickets.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">See the difference immediately</h3>
                <p className="text-gray-700">Your inbox is suddenly organized. You can see what needs attention. Your team knows what's happening.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Common Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Do I have to stop using Gmail?</h3>
              <p className="text-gray-700">No! TicketDesk connects to your Gmail. You can still use Gmail for everything. TicketDesk just adds a better way to manage support emails.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What happens to my existing emails?</h3>
              <p className="text-gray-700">They stay in Gmail. TicketDesk starts tracking new emails as tickets. Your history doesn't change.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I still respond from Gmail?</h3>
              <p className="text-gray-700">Yes! You can respond from either TicketDesk or Gmail. TicketDesk will track the conversation either way.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I decide it's not for me?</h3>
              <p className="text-gray-700">Just disconnect TicketDesk. Your Gmail goes back to exactly how it was. Nothing changes, nothing breaks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Stop Losing Customers to Email Chaos
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Try TicketDesk free - see the difference in 2 minutes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/register" 
              className="bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free (10 Tickets Forever) <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a 
              href="/" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Learn More
            </a>
          </div>
          <p className="text-sm opacity-75 mt-6">
            No credit card required • Connect Gmail in 60 seconds • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">&copy; 2025 TicketDesk. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CompareEmail;